import { canStillBeConfirmed } from '../shared/confirm-deadline';
import { DATE_FORMAT, TIME_FORMAT, dateTime } from '../shared/date-time';
import { eachSlotStart } from '../shared/office-day';
import { AFTERNOON_STARTS_AT_HOUR, EVENING_STARTS_AT_HOUR } from './constants';
import type { CalendarDay, CallbackSlot, MonthQuery, SlotPeriod, SlotsQuery } from './type';

const toPeriod = (hour: number): SlotPeriod => {
  if (hour < AFTERNOON_STARTS_AT_HOUR) return 'morning';
  if (hour < EVENING_STARTS_AT_HOUR) return 'afternoon';
  return 'evening';
};

function toSlot(
  startsAt: string,
  visitorTimeZone: string,
  now: Date,
  takenAt: Set<number>
): CallbackSlot {
  const visitorTime = dateTime(startsAt).tz(visitorTimeZone);
  const instant = visitorTime.valueOf();

  return {
    startsAt,
    label: visitorTime.format(TIME_FORMAT),
    period: toPeriod(visitorTime.hour()),
    available: canStillBeConfirmed(instant, now) && !takenAt.has(instant),
  };
}

const byInstant = (one: string, other: string) => Date.parse(one) - Date.parse(other);

/** Every callback slot of `date`, read in the visitor's zone and ordered by start time. */
export function listDaySlots({
  date,
  settings,
  visitorTimeZone,
  now,
  takenAt = [],
}: SlotsQuery): CallbackSlot[] {
  const taken = new Set(takenAt.map((at) => new Date(at).getTime()));

  return [...eachSlotStart({ date, settings, visitorTimeZone })]
    .sort(byInstant)
    .map((startsAt) => toSlot(startsAt, visitorTimeZone, now, taken));
}

/**
 * Whether `date` still has a slot to offer. Stops at the first free one, and rejects a day
 * already behind the visitor without generating anything — the calendar asks this once per day
 * of the month, so the early exits are what keep it off the render path.
 */
function hasOpenSlot({ date, settings, visitorTimeZone, now, takenAt = [] }: SlotsQuery): boolean {
  if (date < dateTime(now).tz(visitorTimeZone).format(DATE_FORMAT)) return false;

  const taken = new Set(takenAt.map((at) => new Date(at).getTime()));

  for (const startsAt of eachSlotStart({ date, settings, visitorTimeZone })) {
    const instant = dateTime(startsAt).tz(visitorTimeZone).valueOf();
    if (canStillBeConfirmed(instant, now) && !taken.has(instant)) return true;
  }

  return false;
}

/** Every day of `month` (`YYYY-MM`), flagged with whether it still has a free slot. */
export function listMonthDays({ month, ...query }: MonthQuery): CalendarDay[] {
  const firstDay = dateTime(`${month}-01`);

  return Array.from({ length: firstDay.daysInMonth() }, (_, index) => {
    const date = firstDay.add(index, 'day').format(DATE_FORMAT);

    return { date, dayOfMonth: index + 1, selectable: hasOpenSlot({ date, ...query }) };
  });
}
